import {
  Screen,
  useScreenshotsByDeviceIdQuery,
  useScreenshotsByScreenIdQuery,
  useTakeScreenshotByScreenIdMutation,
} from "src/types.g";
import {
  CenterLoaderStyled,
  ScreenshotModalViewStyled,
  TakingScreenshotLoadingStyled,
} from "./styles";
import {
  ScreenshotItem,
  mapDeviceScreenshots,
  mapScreenScreenshots,
} from "./screenshotMapper";
import { PrimaryButton } from "src/helpers/whiteLabel";
import { FormattedMessage } from "react-intl";
import {
  Button,
  Icon,
  Loader,
  SCREEN_STATUS_ENUM,
} from "@screencloud/screencloud-ui-components";
import spacetime from "spacetime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ProcessingFailureThumbnail from "../../../images/processing_failure_thumb.svg";
import { pollForImage } from "src/helpers/pollForImage";
import {
  CommandStatus,
  RDM_COMMAND_TYPE,
  useRdmCommand,
} from "src/contexts/realtime-rdm/useRdmCommand";
import { RealtimeRdmProvider } from "src/contexts/realtime-rdm/RealtimeRdmProvider";
import { ScreenDeviceRdmProvider } from "src/contexts/realtime-rdm/ScreenDeviceRdmProvider";

export const isAllowClearCacheByDevicePlatform = (
  screen: Pick<Screen, "devicePlatform" | "deviceInfo"> | null | undefined,
): boolean => {
  return (
    screen?.devicePlatform?.toLowerCase() === "android" ||
    screen?.devicePlatform?.toLowerCase() === "amazon" ||
    screen?.devicePlatform?.toLowerCase() === "amazonsignage" ||
    (screen?.deviceInfo?.native_player?.capabilities?.clear_cache &&
      screen?.deviceInfo?.native_player?.screen_api_command?.clear_cache)
  );
};

const handleDownload = (url, timestamp) => {
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.download = `screenshot-${timestamp}.jpg`;
  link.click();
};

export const sleep = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export { pollForImage } from "src/helpers/pollForImage";

const TakingScreenshotLoading = () => {
  const [showAttention, setShowAttention] = useState<boolean>(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowAttention(true);
    }, 10 * 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <TakingScreenshotLoadingStyled>
      <CenterLoaderStyled>
        <div className="center">
          <Loader active />
        </div>
      </CenterLoaderStyled>
      <p className="header">Taking screenshot...</p>
      <p className={`description ${showAttention && "show"}`}>
        This shouldn’t take longer than a minute. Please take a new screenshot
        or reach out to our support team for further assistance.
      </p>
    </TakingScreenshotLoadingStyled>
  );
};

const RDM_SCREENSHOT_TIMEOUT_MS = 180_000;

interface ScreenshotModalViewProps {
  screenId: string;
  screenStatus: SCREEN_STATUS_ENUM;
}
const ScreenshotModal = (props: ScreenshotModalViewProps) => {
  const { screenStatus } = props;

  const {
    isRdmOnline,
    isCommandSupported,
    sendRdmCommand,
    isCommandProcessing,
    lastCommand,
    deviceState,
  } = useRdmCommand(props.screenId);

  const canUseRDM =
    isRdmOnline && isCommandSupported(RDM_COMMAND_TYPE.SCREENSHOT);
  const deviceId = deviceState?.deviceId ?? "";

  // Both list hooks are called unconditionally (rules of hooks); the inactive
  // one is skipped. RDM devices list by deviceId, legacy by screenId.
  const deviceQuery = useScreenshotsByDeviceIdQuery({
    variables: { input: { deviceId } },
    skip: !canUseRDM || !deviceId,
    // Since, useScreenshotsByDeviceIdQuery is fetch via deviceId, if we don't set fetchPolicy to network-only, it will use the cached data and not fetch the latest data from the server.
    fetchPolicy: "network-only",
  });
  const screenQuery = useScreenshotsByScreenIdQuery({
    variables: { input: { screenId: props.screenId } },
    skip: canUseRDM,
  });

  const activeQuery = canUseRDM ? deviceQuery : screenQuery;
  const { loading } = activeQuery;
  const refetch = activeQuery.refetch;
  const screenshotsError = canUseRDM ? deviceQuery.error : screenQuery.error;

  // Normalize the two payload shapes (device = data/camelCase, legacy =
  // screenshots/snake_case) into a single render-friendly item. Memoized so the
  // array identity is stable between renders (otherwise effects depending on it
  // re-run every render).
  const screenshots: ScreenshotItem[] = useMemo(
    () =>
      canUseRDM
        ? mapDeviceScreenshots(deviceQuery.data?.screenshotsByDeviceId?.data)
        : mapScreenScreenshots(
            screenQuery.data?.screenshotsByScreenId?.screenshots,
          ),
    [canUseRDM, deviceQuery.data, screenQuery.data],
  );

  const isScreenOnline = screenStatus === SCREEN_STATUS_ENUM.LIVE;

  const [takeScreenshotByScreenId, { loading: isTakingLegacyScreenshot }] =
    useTakeScreenshotByScreenIdMutation();
  const [isPollingScreenshot, setIsPollingScreenshot] =
    useState<boolean>(false);
  // RDM take is async: set on click, cleared when the WS command resolves.
  const [isRdmTaking, setIsRdmTaking] = useState<boolean>(false);
  const rdmTakingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopRdmTaking = useCallback(() => {
    setIsRdmTaking(false);
    if (rdmTakingTimerRef.current) {
      clearTimeout(rdmTakingTimerRef.current);
      rdmTakingTimerRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      if (rdmTakingTimerRef.current) clearTimeout(rdmTakingTimerRef.current);
    },
    [],
  );

  const isTakingScreenshot = canUseRDM
    ? isRdmTaking || isCommandProcessing(RDM_COMMAND_TYPE.SCREENSHOT)
    : isTakingLegacyScreenshot;

  const [isSelectedScreenshotImageError, setIsSelectedScreenshotImageError] =
    useState<boolean>(false);
  const [
    isSelectedScreenshotImageLoading,
    setIsSelectedScreenshotImageLoading,
  ] = useState<boolean>(true);
  const [selectedScreenshotId, setSelectedScreenshotId] = useState<string>("");

  const isNoScreenshotYet = useMemo(
    () => screenshots.length === 0,
    [screenshots.length],
  );

  const selectedScreenshot = useMemo(
    () =>
      screenshots.find((screenshot) => screenshot.id === selectedScreenshotId),
    [screenshots, selectedScreenshotId],
  );

  const selectScreenshotId = useCallback((screenshotId: string) => {
    setSelectedScreenshotId(screenshotId);
    setIsSelectedScreenshotImageLoading(true);
    setIsSelectedScreenshotImageError(false);
  }, []);

  const takeScreenshot = useCallback(async () => {
    // RDM path: dispatch screen.capture via createScreenDeviceCommand. The WS
    // command lifecycle (lastCommand) drives completion + list refetch below.
    if (canUseRDM) {
      setIsRdmTaking(true);
      if (rdmTakingTimerRef.current) clearTimeout(rdmTakingTimerRef.current);
      rdmTakingTimerRef.current = setTimeout(() => {
        setIsRdmTaking(false);
        rdmTakingTimerRef.current = null;
      }, RDM_SCREENSHOT_TIMEOUT_MS);
      selectScreenshotId("");
      await sendRdmCommand(RDM_COMMAND_TYPE.SCREENSHOT);
      return;
    }

    const { data, errors } = await takeScreenshotByScreenId({
      variables: {
        input: {
          screenId: props.screenId,
        },
      },
    });

    if (errors) {
      console.log("Error from taking screenshot:", errors);
      return;
    }

    setIsPollingScreenshot(true);
    await pollForImage(data?.takeScreenshotByScreenId?.screenshot?.url ?? "");
    setIsPollingScreenshot(false);

    selectScreenshotId("");
    refetch();
  }, [
    canUseRDM,
    sendRdmCommand,
    selectScreenshotId,
    takeScreenshotByScreenId,
    refetch,
    props.screenId,
  ]);

  // RDM completion: when the screen.capture command resolves, refetch the
  // deviceId list (success) or stop the spinner (failure).
  useEffect(() => {
    if (!canUseRDM || !isRdmTaking) return;
    if (lastCommand?.commandType !== RDM_COMMAND_TYPE.SCREENSHOT) return;

    if (lastCommand.status === CommandStatus.COMPLETED) {
      stopRdmTaking();
      void refetch();
    } else if (lastCommand.status === CommandStatus.FAILED) {
      stopRdmTaking();
    }
  }, [
    canUseRDM,
    isRdmTaking,
    lastCommand?.commandType,
    lastCommand?.status,
    refetch,
    stopRdmTaking,
  ]);

  if (screenshotsError) {
    console.warn("Screenshots error:", screenshotsError);
  }

  // Auto-take a screenshot once when the gallery opens empty. Guarded by a ref
  // so it fires at most once per empty episode (avoids a take storm while the
  // list is still empty / the command is in flight).
  const hasAutoRequestedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!isScreenOnline || loading) {
      return;
    }

    if (isNoScreenshotYet) {
      if (!hasAutoRequestedRef.current && !isTakingScreenshot) {
        hasAutoRequestedRef.current = true;
        takeScreenshot();
      }
      return;
    }

    hasAutoRequestedRef.current = false;

    const latestScreenshot = screenshots[0];
    if (!latestScreenshot) {
      return;
    }

    selectScreenshotId(latestScreenshot.id ?? "");
  }, [
    screenshots,
    isNoScreenshotYet,
    isScreenOnline,
    loading,
    isTakingScreenshot,
    takeScreenshot,
    selectScreenshotId,
  ]);

  if (isScreenOnline && loading) {
    return (
      <CenterLoaderStyled>
        <Loader active />
      </CenterLoaderStyled>
    );
  }

  return (
    <div className="screenshot-viewer-container">
      <div className={`screenshot-viewer ${isNoScreenshotYet && "empty"}`}>
        {isScreenOnline &&
        (isTakingScreenshot || isPollingScreenshot || !selectedScreenshot) ? (
          <TakingScreenshotLoading />
        ) : (
          <div className="screenshot-preview-container">
            {selectedScreenshot ? (
              <>
                <img
                  loading="lazy"
                  src={selectedScreenshot?.url ?? ""}
                  onLoad={() => setIsSelectedScreenshotImageLoading(false)}
                  onError={(e) => {
                    e.currentTarget.src = ProcessingFailureThumbnail;
                    setIsSelectedScreenshotImageError(true);
                  }}
                />
                <div className="info-container">
                  <div className="info">
                    <div>
                      {spacetime(
                        new Date(selectedScreenshot?.createdAt),
                      ).unixFmt("MMM dd, yyyy, h:mm a")}
                    </div>
                    <div className="taken-by">
                      Taken by {selectedScreenshot?.givenName}{" "}
                      {selectedScreenshot?.familyName}
                    </div>
                  </div>
                  <Button
                    data-testid="screenshot-download-btn"
                    disabled={
                      isSelectedScreenshotImageError ||
                      isSelectedScreenshotImageLoading
                    }
                    onClick={() =>
                      handleDownload(
                        selectedScreenshot?.url,
                        selectedScreenshot?.createdAt,
                      )
                    }
                  >
                    <Icon
                      data-testid="screenshot-preview-download-icon"
                      name="download"
                    />
                    <FormattedMessage
                      id="ui_component.common.label.download"
                      defaultMessage="Download"
                    />
                  </Button>
                </div>
              </>
            ) : (
              <div className="info-container">
                <FormattedMessage
                  id="ui_component.common.label.no_screenshot_yet"
                  defaultMessage="No screenshot yet"
                />
              </div>
            )}
          </div>
        )}
      </div>
      <div className="screenshot-list">
        <div className="header item-padding">
          <FormattedMessage
            id="ui_component.common.label.all_screenshots"
            defaultMessage="ALL SCREENSHOTS"
          />
        </div>
        <div className="y-scrollable">
          {screenshots.map((screenshot) => (
            <div
              className={`screenshot-item item-padding ${
                screenshot.id === selectedScreenshotId && "active"
              }`}
              key={screenshot.id}
              onClick={() => selectScreenshotId(screenshot.id)}
            >
              <img
                loading="lazy"
                src={screenshot.url}
                onError={(e) => {
                  const target = e.currentTarget;
                  target.src = ProcessingFailureThumbnail;
                  const screenshotItemDownloadButton =
                    target.parentElement?.querySelector<HTMLButtonElement>(
                      "[data-testid=screenshot-list-item-download]",
                    );
                  if (!screenshotItemDownloadButton) {
                    return;
                  }
                  screenshotItemDownloadButton.style.display = "none";
                }}
              />
              <div className="screenshot-info">
                <div>
                  {spacetime(new Date(screenshot?.createdAt)).unixFmt(
                    "MMM dd, yyyy, h:mm a",
                  )}
                </div>
                <div>
                  Taken by {screenshot.givenName} {screenshot.familyName}
                </div>
              </div>
              <div className="center">
                <Icon
                  data-testid="screenshot-list-item-download"
                  name="download"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(screenshot.url, screenshot.createdAt);
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="screenshot-action">
          <PrimaryButton
            disabled={
              isTakingScreenshot || isPollingScreenshot || !isScreenOnline
            }
            className="screenshot-btn"
            data-testid="screenshot-btn"
            onClick={takeScreenshot}
          >
            <Icon data-testid="screenshot-modal-screenshot" name="camera" />
            <FormattedMessage
              id="ui_component.common.label.new_screenshot"
              defaultMessage="New Screenshot"
            />
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export const ScreenshotModalView = (props: ScreenshotModalViewProps) => {
  return (
    <RealtimeRdmProvider>
      <ScreenDeviceRdmProvider screenId={props.screenId}>
        <ScreenshotModalViewStyled>
          <ScreenshotModal {...props} />
        </ScreenshotModalViewStyled>
      </ScreenDeviceRdmProvider>
    </RealtimeRdmProvider>
  );
};
